import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Plus, Trash2, Activity, X, Clock, User } from 'lucide-react';

export default function InterventionsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [machines, setMachines] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    machine_id: '', symptom: '', diagnosis: '', solution: '',
    parts_used: '', time_spent_min: 30, technician_notes: '',
  });

  const load = () => api.get('/interventions').then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get('/machines').then((r) => setMachines(r.data));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/interventions', { ...form, time_spent_min: Number(form.time_spent_min) });
      toast.success('Intervento registrato');
      setOpen(false);
      setForm({ machine_id: '', symptom: '', diagnosis: '', solution: '', parts_used: '', time_spent_min: 30, technician_notes: '' });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Errore'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Eliminare questo intervento?')) return;
    await api.delete(`/interventions/${id}`);
    toast.success('Intervento eliminato');
    load();
  };

  return (
    <div className="animate-fade-up">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-[#002FA7]">REGISTRO</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-2">Interventi</h1>
          <p className="text-gray-500 mt-2">Storico completo degli interventi tecnici.</p>
        </div>
        <button onClick={() => setOpen(true)} disabled={machines.length === 0} className="btn-primary" data-testid="add-intervention-btn">
          <Plus className="w-4 h-4" /> Nuovo intervento
        </button>
      </div>

      {machines.length === 0 && items.length === 0 && (
        <div className="mt-6 border border-yellow-300 bg-yellow-50 p-4 text-sm">
          Aggiungi prima una macchina nell'archivio per poter registrare interventi manualmente.
        </div>
      )}

      <div className="mt-8 space-y-3" data-testid="interventions-list">
        {items.length === 0 ? (
          <div className="surface p-12 text-center">
            <Activity className="w-10 h-10 text-gray-300 mx-auto" strokeWidth={1.2} />
            <div className="mt-3 font-display font-bold text-lg">Nessun intervento registrato</div>
          </div>
        ) : items.map((it) => (
          <div key={it.id} className="surface p-5" data-testid={`intervention-${it.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="font-display font-bold text-lg tracking-tight truncate">{it.machine_label}</div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-gray-500">
                  <span className="flex items-center gap-1"><User className="w-3 h-3" /> {it.technician_name}</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {it.time_spent_min} min</span>
                  <span className="font-mono">{new Date(it.created_at).toLocaleString('it-IT')}</span>
                </div>
              </div>
              {(user?.role === 'admin' || user?.id === it.created_by) && (
                <button onClick={() => remove(it.id)} className="btn-ghost text-red-600" data-testid={`del-int-${it.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 text-sm">
              <div>
                <div className="overline text-gray-500 mb-1">Sintomo</div>
                <div>{it.symptom}</div>
              </div>
              <div>
                <div className="overline text-gray-500 mb-1">Diagnosi</div>
                <div>{it.diagnosis}</div>
              </div>
              <div>
                <div className="overline text-gray-500 mb-1">Soluzione</div>
                <div>{it.solution}</div>
              </div>
            </div>
            {(it.parts_used || it.technician_notes) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-100 text-sm">
                {it.parts_used && <div><div className="overline text-gray-500 mb-1">Ricambi usati</div><div className="font-mono text-xs">{it.parts_used}</div></div>}
                {it.technician_notes && <div><div className="overline text-gray-500 mb-1">Note tecnico</div><div className="text-gray-700">{it.technician_notes}</div></div>}
              </div>
            )}
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setOpen(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
            className="bg-white w-full sm:max-w-2xl border border-gray-200 max-h-[95vh] overflow-y-auto" data-testid="int-form">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="font-display font-bold text-lg">Nuovo intervento</div>
              <button type="button" onClick={() => setOpen(false)} className="btn-ghost"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="overline text-gray-600 block mb-1">Macchina *</label>
                <select required className="input-base" value={form.machine_id} onChange={(e) => setForm({ ...form, machine_id: e.target.value })} data-testid="int-machine">
                  <option value="">—</option>
                  {machines.map((m) => <option key={m.id} value={m.id}>{m.brand} {m.model} · {m.serial_number}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="overline text-gray-600 block mb-1">Sintomo *</label>
                  <textarea rows={2} required className="input-base" value={form.symptom} onChange={(e) => setForm({ ...form, symptom: e.target.value })} data-testid="int-symptom" />
                </div>
                <div>
                  <label className="overline text-gray-600 block mb-1">Diagnosi *</label>
                  <textarea rows={2} required className="input-base" value={form.diagnosis} onChange={(e) => setForm({ ...form, diagnosis: e.target.value })} data-testid="int-diagnosis" />
                </div>
              </div>
              <div>
                <label className="overline text-gray-600 block mb-1">Soluzione *</label>
                <textarea rows={2} required className="input-base" value={form.solution} onChange={(e) => setForm({ ...form, solution: e.target.value })} data-testid="int-solution" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="overline text-gray-600 block mb-1">Ricambi usati</label>
                  <input className="input-base font-mono" value={form.parts_used} onChange={(e) => setForm({ ...form, parts_used: e.target.value })} data-testid="int-parts" />
                </div>
                <div>
                  <label className="overline text-gray-600 block mb-1">Tempo impiegato (min)</label>
                  <input type="number" min="0" className="input-base font-mono" value={form.time_spent_min} onChange={(e) => setForm({ ...form, time_spent_min: e.target.value })} data-testid="int-time" />
                </div>
              </div>
              <div>
                <label className="overline text-gray-600 block mb-1">Note tecnico</label>
                <textarea rows={2} className="input-base" value={form.technician_notes} onChange={(e) => setForm({ ...form, technician_notes: e.target.value })} data-testid="int-notes" />
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="btn-secondary">Annulla</button>
              <button type="submit" className="btn-primary" data-testid="int-submit">Registra</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
